import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useCart } from "@/lib/cart";
import { api, errMsg, imgUrl, inr, tokens, tokenHeaders } from "@/lib/api";
import { startPayment } from "@/lib/pay";

const FIELDS = [
  ["name", "Full name", "sm:col-span-2"], ["mobile", "Mobile number", ""], ["email", "Email address", ""],
  ["address", "House no., street, area", "sm:col-span-2"], ["landmark", "Landmark (optional)", ""], ["city", "City / Town", ""],
  ["district", "District", ""], ["state", "State", ""], ["pincode", "PIN code", ""],
];

export default function Checkout() {
  const { priced, items, cartId, clear } = useCart();
  const navigate = useNavigate();
  const [c, setC] = useState({ name: "", mobile: "", email: "", address: "", landmark: "", city: "", district: "", state: "", pincode: "" });
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);
  const [payments, setPayments] = useState(true);
  useEffect(() => {
    api.get("/public-config").then((r) => setPayments(r.data.payments)).catch(() => {});
  }, []);
  const unavailable = priced.items.some((l) => !l.available);

  const pay = async () => {
    setBusy(true);
    let o = order;
    try {
      if (!o) {
        const r = await api.post("/orders", { items, customer: c, accept_terms: accept, cart_id: cartId, referral_code: localStorage.getItem("bmi_ref") || undefined });
        o = r.data;
        tokens.set("order", o.id, o.access_token);
        setOrder(o);
      }
      const res = await startPayment({ base: `/orders/${o.id}`, headers: tokenHeaders("order", o.id), description: `Order ${o.order_number}` });
      if (res.status === "paid") clear();
      navigate(`/order/${o.id}?result=${res.status}`);
    } catch (e) {
      toast.error(errMsg(e));
      if (o) navigate(`/order/${o.id}?result=error`);
    } finally {
      setBusy(false);
    }
  };

  if (!priced.items.length)
    return (
      <div className="container-x py-24 text-center" data-testid="checkout-empty">
        <h1 className="text-3xl font-bold">Your cart is empty</h1>
        <Link to="/products" className="mt-4 inline-block rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white">Browse products</Link>
      </div>
    );

  return (
    <div className="container-x grid gap-10 py-16 lg:grid-cols-12">
      <section className="space-y-6 lg:col-span-7">
        <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">Checkout</h1>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="mb-4 font-display text-lg font-bold">Delivery details</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map(([k, label, span]) => (
              <div key={k} className={`space-y-1.5 ${span}`}>
                <Label htmlFor={k}>{label}</Label>
                <Input id={k} value={c[k]} disabled={!!order} onChange={(e) => setC({ ...c, [k]: e.target.value })} data-testid={`checkout-${k}-input`} />
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-500">We only collect what is needed to deliver your order and send you updates.</p>
        </div>
      </section>
      <aside className="lg:col-span-5">
        <div className="sticky top-24 space-y-4 rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-display text-lg font-bold">Review your order</h2>
          {priced.items.map((l) => (
            <div key={l.sku} className="flex items-center gap-3 text-sm">
              <img src={imgUrl(l.image)} alt="" className="h-12 w-12 rounded-lg object-cover" />
              <div className="flex-1"><p className="font-semibold">{l.name}</p><p className="text-xs text-slate-500">{l.size} × {l.qty}</p>{!l.available && <p className="text-xs text-red-600">Only {l.stock} in stock</p>}</div>
              <span className="font-mono-num">{inr(l.line_total)}</span>
            </div>
          ))}
          <div className="space-y-1.5 border-t border-slate-100 pt-4 text-sm">
            <div className="flex justify-between"><span>Item total (MRP)</span><span className="font-mono-num">{inr(priced.mrp_total)}</span></div>
            {priced.discount > 0 && <div className="flex justify-between text-emerald-700"><span>Discount</span><span className="font-mono-num">−{inr(priced.discount)}</span></div>}
            <div className="flex justify-between"><span>Delivery</span><span className="font-mono-num">{inr(priced.shipping)}</span></div>
            <div className="flex justify-between border-t border-slate-100 pt-2 text-lg font-bold"><span>Payable amount</span><span className="font-mono-num" data-testid="checkout-total">{inr(priced.total)}</span></div>
          </div>
          <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600" data-testid="payment-method-info">
            <p className="font-semibold text-slate-900">Payment method: Online via Razorpay</p>
            <p>UPI, cards, net banking or wallets as enabled on our Razorpay account. Cash on Delivery is not available. Your order is confirmed only after the payment is verified.</p>
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={accept} onCheckedChange={(v) => setAccept(!!v)} disabled={!!order} data-testid="checkout-accept-terms" />
            <span>I have read the <Link to="/policies/shipping" className="underline">Shipping</Link>, <Link to="/policies/product-returns" className="underline">Return & Refund</Link> and <Link to="/policies/cancellation" className="underline">Cancellation</Link> policies.</span>
          </label>
          {!payments && <p className="rounded-xl bg-red-50 p-3 text-sm font-semibold text-red-800" data-testid="checkout-payments-not-configured">Online payment is not configured yet, so orders cannot be placed right now. Please check back soon.</p>}
          <Button className="h-12 w-full rounded-full bg-amber-600 text-base hover:bg-amber-700" disabled={busy || !accept || unavailable || !payments} onClick={pay} data-testid="checkout-pay-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Pay {inr(priced.total)} securely
          </Button>
        </div>
      </aside>
    </div>
  );
}
